import { BsInfoLg } from "react-icons/bs";
import { NotificationData, notifications } from "@mantine/notifications";
import { IconCheck, IconX } from "@tabler/icons-react";
import i18n from "src/i18n";

import { AUTO_CLOSE } from "@constants/index";


export type Variant = "query" | "mutation";
interface NotificationProps {
  id?: string;
  title?: string;
  message: string;
  variant?: Variant;
}
export const showErrorNotification = ({
  id,
  title,
  message,
  variant,
}: NotificationProps) => {
  const options: NotificationData = {
    ...(title && {
      title: variant
        ? i18n.t(title.trim(), { context: variant })
        : i18n.t(title.trim()),
    }),
    message: variant
      ? i18n.t(message.trim(), { context: variant })
      : i18n.t(message.trim()),
    withBorder: true,
    icon: <IconX size={18} />,
    color: "red",
    autoClose: 1000 * 30 * 1, //* Rather than let it stay forever, let it stay for a long time
  };

  if (id) {
    // If id is provided, update the notification
    notifications.update({
      id,
      loading: false,
      ...options,
    });

    //* This timeout was intended to clear errors when there's finally a success notification, but it also clears success notifications that come after it which is unintended
    setTimeout(() => {
      // Hide all other notifications after 1 succeeds. This is to clear other "loading" notifications if the user performs an action multiple times
      notifications.clean();
    }, AUTO_CLOSE);
  } else {
    notifications.show({
      ...options,
    });
  }
};

export const showSuccessNotification = ({
  id,
  title,
  message,
}: Pick<NotificationProps, "id" | "title" | "message">) => {
  const options: NotificationData = {
    ...(title && {
      title: i18n.t(title.trim()),
    }),
    message: i18n.t(message.trim()),
    withBorder: true,
    icon: <IconCheck size={18} />,
    color: "green",
  };

  if (id) {
    // If id is provided, update the notification
    notifications.update({
      id,
      loading: false,
      autoClose: true,
      ...options,
    });

    // * This timeout causes an issue where when a success notification is cleaned, other pending notifications are cleaned up too
    // setTimeout(() => {
    //   // Hide all other notifications after 1 succeeds. This is to clear other "loading" notifications if the user performs an action multiple times
    //   notifications.clean();
    // }, AUTO_CLOSE);
  } else {
    notifications.show({
      ...options,
    });
  }
};

export const showInfoNotification = ({
  title,
  message,
  autoClose,
}: Pick<NotificationProps, "title" | "message"> & {
  autoClose?: number | boolean;
}) => {
  notifications.show({
    ...(title && {
      title: i18n.t(title.trim()),
    }),
    message: i18n.t(message.trim()),
    withBorder: true,
    icon: <BsInfoLg size={18} />,
    color: "blue",
    ...(autoClose !== undefined && { autoClose }),
  });
};

export const showLoadingNotification = ({
  id,
  title = "just-a-moment",
  message = "processing-request",
}: Partial<Pick<NotificationProps, "id" | "title" | "message">>) => {
  const options: NotificationData = {
    title: i18n.t(title.trim()),
    message: i18n.t(message.trim()),
    withBorder: true,
    color: "gray.5",
    loading: true,
    autoClose: false,
  };

  if (id) {
    return notifications.update({
      id,
      ...options,
    });
  } else {
    return notifications.show({
      ...options,
    });
  }
};
